import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Typography
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { DataContext } from '../context/DataContext';
import AgentService from '../services/AgentService';

/**
 * Muestra, debajo del selector de Workspace, el agente que usa el chat para
 * el workspace seleccionado. Si el workspace tiene 2+ agentes ACTIVE, permite
 * elegir cuál usar. La elección se persiste por workspace (DataContext ->
 * localStorage) hasta que el usuario elija otra.
 */
const AgentSelector = () => {
  const {
    selectedWorkspace,
    agentByWorkspace,
    updateSelectedAgent,
    isPlatformRoot,
    isTenantAdmin
  } = useContext(DataContext);
  const navigate = useNavigate();

  const workspaceId = selectedWorkspace?.semantic_workspace_id || null;

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (!workspaceId) {
      setAgents([]);
      setError(null);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError(null);
    // listAllAgents: esto llena un selector, así que un agente que no
    // entrara en la primera página simplemente no se podría elegir.
    AgentService.listAllAgents({ workspaceId })
      .then((res) => {
        if (!mounted) return;
        const list = (res?.agents || [])
          .filter((a) => a.agentStatus === 'ACTIVE')
          .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
        setAgents(list);
      })
      .catch((err) => {
        if (!mounted) return;
        console.error('AgentSelector.listAgents', err);
        setError('No se pudieron cargar los agentes del workspace.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [workspaceId]);

  // Id efectivo: la elección guardada si sigue existiendo, si no el más reciente.
  const effectiveId = useMemo(() => {
    if (agents.length === 0) return null;
    const stored = workspaceId ? agentByWorkspace[workspaceId] : null;
    if (stored && agents.some((a) => a.agentId === stored)) return stored;
    return agents[0].agentId;
  }, [agents, agentByWorkspace, workspaceId]);

  // Sincroniza el contexto si hubo fallback (agente borrado, primera vez, etc.)
  useEffect(() => {
    if (!workspaceId || !effectiveId) return;
    if (agentByWorkspace[workspaceId] !== effectiveId) {
      updateSelectedAgent(workspaceId, effectiveId);
    }
  }, [workspaceId, effectiveId, agentByWorkspace, updateSelectedAgent]);

  if (!workspaceId) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <CircularProgress size={16} sx={{ color: 'var(--wb-primary)' }} />
        <Typography variant="caption" color="var(--wb-text-soft)">Cargando agentes...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ py: 0.25, mt: 1 }}>{error}</Alert>;
  }

  if (agents.length === 0) {
    const canManage = isPlatformRoot || isTenantAdmin;
    return (
      <Alert
        severity="info"
        sx={{ py: 0.25, mt: 1 }}
        action={
          canManage ? (
            <Button color="inherit" size="small" onClick={() => navigate('/admin/home/agentes/listado')}>
              Configurar
            </Button>
          ) : undefined
        }
      >
        Este workspace no tiene un agente configurado.
      </Alert>
    );
  }

  if (agents.length === 1) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, px: 0.5 }}>
        <SmartToyOutlinedIcon sx={{ fontSize: 16, color: 'var(--wb-text-faint)' }} />
        <Typography variant="caption" sx={{ color: 'var(--wb-x-text-ffffffd9, rgba(255,255,255,0.85))' }}>
          Agente: <strong>{agents[0].agentName || agents[0].agentId}</strong>
        </Typography>
      </Box>
    );
  }

  return (
    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
      <InputLabel
        id="agent-selector-label"
        sx={{ color: 'var(--wb-text-faint)', '&.Mui-focused': { color: 'var(--wb-primary)' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
          Agente
        </Box>
      </InputLabel>
      <Select
        labelId="agent-selector-label"
        id="agent-selector"
        value={effectiveId || ''}
        onChange={(e) => updateSelectedAgent(workspaceId, e.target.value)}
        label="Agente"
        sx={{
          color: 'var(--wb-text-strong)',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-outline)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-text-ghost)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-primary)' },
          '& .MuiSvgIcon-root': { color: 'var(--wb-text-faint)' }
        }}
      >
        {agents.map((agent) => (
          <MenuItem key={agent.agentId} value={agent.agentId}>
            {agent.agentName || agent.semanticModelName || agent.agentId}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default AgentSelector;
